import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, TrendingUp, TrendingDown, Trash2, Wallet, Layers, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { Card, Spinner, EmptyState, Badge, Button } from "../components/ui/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { useOutlets } from "../components/OutletContext";

const TYPE_LABEL = {
  restaurant: "Restaurant",
  bar: "Bar",
  hotel: "Hotel",
  cafe: "Café",
  catering: "Catering",
  other: "Sonstiges",
};

function fmtEUR(n) {
  return new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
}

function KPI({ icon: Icon, label, value, trend, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-ink-primary tabular-nums">{value}</div>
          {trend && <div className="mt-1 text-xs text-ink-secondary">{trend}</div>}
        </div>
        {Icon && <Icon size={18} className="text-brand opacity-70" strokeWidth={1.5} />}
      </div>
    </Card>
  );
}

export default function UnifiedDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { outlets, switchOutlet } = useOutlets();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isOwner = !user?.team_owner_id;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/outlets/unified-dashboard");
        setData(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleOpenOutlet = (id) => {
    switchOutlet(id);
    navigate("/dashboard");
  };

  if (!isOwner) {
    return (
      <div className="space-y-6" data-testid="unified-dashboard-page">
        <EmptyState title={t("unified.owner_only_title", "Nur für Inhaber")} description={t("unified.owner_only_desc", "Diese Übersicht ist nur für den Account-Inhaber sichtbar.")} />
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Spinner size={28} /></div>;
  }
  if (error) {
    return (
      <div className="p-6 rounded-md border border-err/30 bg-err/10 text-err text-sm" data-testid="unified-dashboard-error">
        {error}
      </div>
    );
  }

  const totals = data?.totals || {};
  const list = data?.outlets || [];
  const isMulti = !!data?.is_multi_outlet;

  return (
    <div className="space-y-6" data-testid="unified-dashboard-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand font-medium mb-1">Multi-Outlet</div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("unified.title", "Unified Owner Dashboard")}</h1>
          <p className="text-sm text-ink-secondary mt-1">
            {isMulti
              ? t("unified.subtitle_multi", "Konsolidierte KPIs über alle Ihre Outlets")
              : t("unified.subtitle_single", "Übersicht für Ihren Betrieb")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={isMulti ? "brand" : "default"} data-testid="unified-outlet-count">
            {list.length} {list.length === 1 ? "Outlet" : "Outlets"}
          </Badge>
          <Button variant="outline" onClick={() => navigate("/outlets")} data-testid="manage-outlets-btn">
            {t("unified.manage", "Outlets verwalten")}
          </Button>
        </div>
      </header>

      {/* Consolidated KPIs */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          icon={TrendingDown}
          label={t("unified.avg_fc", "Ø Food Cost %")}
          value={`${(totals.avg_food_cost_pct || 0).toFixed(1)}%`}
          trend={(totals.avg_food_cost_pct || 0) <= 30 ? "Innerhalb Ziel" : "Über Zielwert"}
          testId="kpi-avg-fc"
        />
        <KPI
          icon={Wallet}
          label={t("unified.total_wareneinsatz", "Wareneinsatz gesamt")}
          value={fmtEUR(totals.total_wareneinsatz_eur)}
          testId="kpi-wareneinsatz"
        />
        <KPI
          icon={Trash2}
          label={t("unified.total_waste", "Verluste gesamt")}
          value={fmtEUR(totals.total_waste_eur)}
          testId="kpi-waste"
        />
        <KPI
          icon={TrendingUp}
          label={t("unified.total_db1", "DB1 gesamt")}
          value={fmtEUR(totals.total_db1_eur)}
          testId="kpi-db1"
        />
      </section>

      {/* Per-outlet breakdown */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-ink-primary uppercase tracking-wider">
            {t("unified.by_outlet", "Aufschlüsselung pro Outlet")}
          </h2>
          <span className="text-xs text-ink-muted">{t("unified.click_to_switch", "Klick auf eine Karte: aktives Outlet wechseln")}</span>
        </div>

        {list.length === 0 ? (
          <EmptyState title={t("unified.no_outlets_title", "Keine Outlets")} description={t("unified.no_outlets_desc", "Legen Sie ein Outlet an, um zu starten.")} />
        ) : (
          <>
            <OutletComparisonChart outlets={list} targetFC={user?.target_food_cost || 30} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {list.map((o) => {
              const fcOver = (o.food_cost_pct || 0) > 35;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => handleOpenOutlet(o.id)}
                  className="text-left transition-transform hover:-translate-y-0.5"
                  data-testid={`outlet-card-${o.id}`}
                >
                  <Card className="p-4 hover:border-brand/40 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-md bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
                          <Building2 size={16} strokeWidth={1.6} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink-primary">{o.name}</div>
                          <div className="text-[11px] text-ink-secondary">{TYPE_LABEL[o.type] || o.type}</div>
                        </div>
                      </div>
                      {o.is_default && <Badge tone="brand">Standard</Badge>}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                      <div>
                        <div className="text-ink-muted">Food Cost %</div>
                        <div className={`font-semibold tabular-nums ${fcOver ? "text-warn" : "text-ink-primary"}`}>
                          {(o.food_cost_pct || 0).toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-ink-muted">DB1</div>
                        <div className="font-semibold tabular-nums text-ink-primary">{fmtEUR(o.db1_eur)}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted">Wareneinsatz</div>
                        <div className="font-semibold tabular-nums text-ink-primary">{fmtEUR(o.wareneinsatz_eur)}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted">Verluste</div>
                        <div className="font-semibold tabular-nums text-ink-primary">{fmtEUR(o.waste_eur)}</div>
                      </div>
                    </div>
                  </Card>
                </button>
              );
            })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// Phase 26 — Inline SVG comparison chart (no library)
function OutletComparisonChart({ outlets, targetFC = 30 }) {
  // Three metric groups: Food Cost %, DB1, Waste
  const maxFC = Math.max(targetFC * 1.5, ...outlets.map(o => o.food_cost_pct || 0), 1);
  const maxDB1 = Math.max(1, ...outlets.map(o => o.db1_eur || 0));
  const maxWaste = Math.max(1, ...outlets.map(o => o.waste_eur || 0));

  const bar = (value, max, colorFn, label, isPct = false) => {
    const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-5 rounded-md bg-white/5 overflow-hidden border border-white/5">
          <div className={`h-full ${colorFn(value)} transition-[width] duration-500`} style={{ width: `${w}%` }} />
        </div>
        <div className="w-20 text-right tabular-nums text-[11px] text-ink-secondary">
          {isPct ? `${value.toFixed(1)}%` : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value)}
        </div>
      </div>
    );
  };

  const fcColor = (v) => {
    if (v <= targetFC) return "bg-ok/70";
    if (v <= targetFC * 1.05) return "bg-warn/70";
    return "bg-err/70";
  };
  const db1Color = () => "bg-brand/70";
  const wasteColor = (v) => {
    if (v < 50) return "bg-ok/70";
    if (v < 200) return "bg-warn/70";
    return "bg-err/70";
  };

  return (
    <div className="rounded-lg border border-white/10 bg-bg-void/40 p-4 mb-4" data-testid="outlet-comparison-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-ink-primary uppercase tracking-wider">Vergleich</div>
        <div className="text-[10px] text-ink-muted">
          Ziel Food Cost: <span className="text-brand font-semibold">{targetFC}%</span> · grün ≤ Ziel · gelb +5% · rot darüber
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: "Food Cost %", key: "food_cost_pct", max: maxFC, colorFn: fcColor, isPct: true, showTarget: true },
          { title: "DB1 (EUR)", key: "db1_eur", max: maxDB1, colorFn: db1Color },
          { title: "Verluste (EUR)", key: "waste_eur", max: maxWaste, colorFn: wasteColor },
        ].map((metric) => (
          <div key={metric.key} data-testid={`compare-metric-${metric.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">{metric.title}</div>
            <div className="space-y-1.5 relative">
              {metric.showTarget && (
                <div
                  className="absolute top-0 bottom-0 border-l border-dashed border-brand/40 pointer-events-none"
                  style={{ left: `${(targetFC / metric.max) * 100}%` }}
                  data-testid="target-fc-line"
                  aria-hidden
                />
              )}
              {outlets.map((o) => (
                <div key={o.id}>
                  <div className="text-[10px] text-ink-muted truncate">{o.name}</div>
                  {bar(o[metric.key] || 0, metric.max, metric.colorFn, o.name, metric.isPct)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
